import {
  Accordion,
  AccordionSummary,
  FormControlLabel,
  AccordionDetails,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
} from '@mui/material';
import React from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, GroupByIncomeReceived, MdaReportTypeField, MdaReportTypes, SortByIncomeReceived } from '../../store/types';
import { ExpandMore } from '@mui/icons-material';
import { DateRange } from 'src/store';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface IncomeReceivedProps extends BaseReportComponentProps {
  expandIncomeReceived: ActionCreatorWithoutPayload<string>;
  setIncomeReceivedSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setIncomeReceivedExcludeCashFilterOptionsParameter: ActionCreatorWithPayload<boolean, string>;
  setIncomeReceivedGroupByParameter: ActionCreatorWithPayload<GroupByIncomeReceived, string>;
  setIncomeReceivedSortByParameter: ActionCreatorWithPayload<SortByIncomeReceived, string>;
  setDateRangeIncomeReceivedParameter: ActionCreatorWithPayload<DateRange, string>;
}

export const IncomeReceived = ({
  parameters,
  setIncomeReceivedSelectedParameter,
  setIncomeReceivedExcludeCashFilterOptionsParameter,
  setIncomeReceivedSortByParameter,
  setIncomeReceivedGroupByParameter,
  setDateRangeIncomeReceivedParameter,
  createReportSortRequest,
  expandIncomeReceived,
  setGroupConsolidatedOption,
}: IncomeReceivedProps): JSX.Element => {
  const handleClickIncomeReceivedSelect = () => {
    setIncomeReceivedSelectedParameter(!parameters.selectedReports?.incomeReceived.selected);
    createReportSortRequest(
      !parameters.selectedReports?.incomeReceived.selected,
      MdaReportTypes.IncomeReceived.id,
      MdaReportTypes.IncomeReceived.displayName
    );
  };

  const handleClickIncomeReceivedExcludeCashFilterOptions = () => {
    setIncomeReceivedExcludeCashFilterOptionsParameter(!parameters.selectedReports?.incomeReceived.excludeCash);
  };

  const handleClickReportDateIncomeReceived = (dates: DateRange) => {
    setDateRangeIncomeReceivedParameter({
      dateFrom: dates.dateFrom,
      dateTo: dates.dateTo,
      dateRangeType: dates.dateRangeType,
    });
  };

  return (
    <Accordion onChange={expandIncomeReceived} expanded={parameters.selectedReports?.incomeReceived.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.incomeReceived.selected}
              onChangeHandler={handleClickIncomeReceivedSelect}
            />
          }
          label="Income Received"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: parameters.selectedReports.incomeReceived.fromDate,
              dateTo: parameters.selectedReports.incomeReceived.toDate,
              dateRangeType: parameters.selectedReports.incomeReceived.dateRangeType,
            }}
            inceptionDate={parameters.inceptionDate}
            setDateRange={handleClickReportDateIncomeReceived}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={2}>
          <Grid item container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="group-by-id">Group by</InputLabel>
                <WO2Select
                  labelId="group-by-id"
                  label="group by"
                  onChange={(e) => setIncomeReceivedGroupByParameter(e.target.value as GroupByIncomeReceived)}
                  value={parameters.selectedReports.incomeReceived.groupBy}
                >
                  <MenuItem value={GroupByIncomeReceived.Account}>Account</MenuItem>
                  <MenuItem value={GroupByIncomeReceived.Date}>Date</MenuItem>
                  <MenuItem value={GroupByIncomeReceived.IncomeType}>Income Type</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sort-by-id">Sort by</InputLabel>
                <WO2Select
                  label="Sort by"
                  labelId="sort-by-id"
                  onChange={(e) => setIncomeReceivedSortByParameter(e.target.value as SortByIncomeReceived)}
                  value={parameters.selectedReports.incomeReceived.sortBy}
                >
                  <MenuItem value={SortByIncomeReceived.Account}>Account</MenuItem>
                  <MenuItem value={SortByIncomeReceived.DateAscending}>Date Ascending</MenuItem>
                  <MenuItem value={SortByIncomeReceived.DateDescending}>Date Descending</MenuItem>
                  <MenuItem value={SortByIncomeReceived.IncomeType}>Income Type</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid item container direction="row" alignItems="center" spacing={1}>
            <Grid item container alignItems="center" xs={6} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.incomeReceived.excludeCash}
                onChange={handleClickIncomeReceivedExcludeCashFilterOptions}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude Cash</Typography>
            </Grid>
          </Grid>

          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column">
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.incomeReceived.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.IncomeReceived}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
